/**
 * IDB 이벤트 API 를 promise 로 바꾸는 얇은 경계.
 *
 * Owns:
 * - `IDBRequest` 의 success/error 와 `IDBTransaction` 의 complete/abort 를 resolve·reject 로
 *   바꾸는 규칙.
 * - 사유 없이 오는 중단을 분류할 수 있는 오류로 만드는 것.
 *
 * Does not own:
 * - 오류를 어떻게 읽을지(`_errors`), 트랜잭션을 언제 열지(`_bundle`).
 *
 * **request 오류를 무시하지 않는다.** request 의 error 이벤트를 취소하지 않으면 IDB 는 그
 * 트랜잭션을 중단한다. 이 계층은 이 동작을 원한다. 한 op 가 실패하면 묶음 전체가 롤백되어야 한다.
 * 그래서 `preventDefault()` 를 호출하지 않는다.
 */

export function requestDone<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(reasonFor(request.error));
  });
}

/**
 * 트랜잭션의 끝을 기다린다.
 *
 * `error` 는 구독하지 않는다. request 의 error 이벤트는 트랜잭션까지 버블한다. 하지만 그 시점의
 * `transaction.error` 는 아직 비어 있어서 사유를 잃는다. 중단 사유는 `abort` 에서 전달된다.
 * 그래서 끝을 판정하는 곳은 complete 와 abort 둘뿐이다.
 */
export function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(reasonFor(transaction.error));
  });
}

// 명시적 abort() 는 사유가 null 이다. 분류기가 읽을 값을 만들어 정상 중단으로 분류되게 한다.
function reasonFor(error: DOMException | null): DOMException {
  return error ?? new DOMException("IndexedDB work was aborted.", "AbortError");
}
